/**
 * The `Create` component is a dynamic form-based component for managing lease records.
 * It supports three modes: CREATE, EDIT, and VIEW, allowing users to create, edit, or view lease records.
 * The component integrates with various APIs and Redux state to handle data fetching, submission, and updates.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.screen='CREATE'] - The mode of the component. Can be 'CREATE', 'EDIT', or 'VIEW'.
 *
 * @returns {JSX.Element} The rendered `Create` component.
 *
 * @requires dayjs
 * @requires dayjs/plugin/utc
 * @requires @mui/material
 * @requires @mui/material/styles
 * @requires antd
 * @requires lodash
 * @requires react
 * @requires react-redux
 * @requires react-router-dom
 * @requires react-toastify
 * @requires react-number-format
 * @requires store/features/api/apiSlice
 * @requires store/features/process/processApi
 * @requires store/features/api/graphSlice
 * @requires components/dev/AntdValuePreview
 * @requires components/header/DetailPageHeader
 * @requires components/InputLabel/index
 * @requires components/MainCard
 * @requires config
 * @requires pages/comptroller/ComptrollerConfirmModal
 * @requires assets/images/solution_white.svg
 * @requires assets/images/table-icons/edit_green.svg
 * @requires ./leaseRecodTable
 * @requires ./CustomComponents/CardAccordion
 * @requires ./CustomComponents/LeaseBulkUpload
 * @requires ./CustomComponents/LeaseDetailsShowAll
 * @requires ./root
 *
 * @example
 * // Usage in a parent component
 * <Create screen="CREATE" />
 *
 * @example
 * // Usage in EDIT mode
 * <Create screen="EDIT" />
 *
 * @example
 * // Usage in VIEW mode
 * <Create screen="VIEW" />
 *
 * @function handleSubmit
 * Handles form submission based on the current action (create, edit, approve, reject, etc.).
 * @param {Object} data - The form data to be submitted.
 *
 * @function handleLeaseDateChange
 * Automatically calculates and updates the lease end date based on the lease start date and lease term.
 *
 * @function utcConverter
 * Converts a dayjs date object to UTC format.
 * @param {Object} dayjsDate - The dayjs date object to convert.
 * @returns {Object} The UTC-converted dayjs date object.
 *
 * @function handlePreview
 * Prepares and sends a payload to populate the lease schedule based on the form data.
 *
 * @function initialDataLoad
 * Loads initial data for non-CREATE modes and populates the lease schedule if applicable.
 *
 * @function handleAddRow
 * Adds a new row to the lease changes table for modifications.
 *
 * @function queryV3Loader
 * Fetches lease schedule data for VIEW mode based on the selected version.
 *
 * @function handleInputMonthChange
 * Calculates and updates the input change month based on the effective date.
 * @param {Object} e - The date change event.
 *
 * @function handleShowAll
 * Fetches and displays the total lease view data for non-CREATE modes.
 *
 * @function SingleUpload
 * Renders the single entry form for lease management.
 *
 * @constant {Array} items
 * The tab items for the CREATE mode, including Single Entry and Bulk Upload.
 *
 * @useEffect
 * Various useEffect hooks are used to handle data fetching, form initialization, and API responses.
 */

/* eslint-disable no-unused-vars */
import { Box } from '@mui/material';
import { Form, Spin, Tabs } from 'antd';
import solution from 'assets/images/solution_white.svg';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import DetailPageHeader from 'components/header/DetailPageHeader';
import MainCard from 'components/MainCard';
import { toastConfig } from 'config';
import { isEmpty } from 'lodash';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import {
	useGetIdentityHeaderMutation,
	useGetLynFormPropertyMutation,
	useGetSingleIdentityMutation,
	useIdentityBulkMutation,
} from 'store/features/process/processApi';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';

import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import CardAccordion from './CustomComponents/CardAccordion';
import LeaseDetailsShowAll from './CustomComponents/LeaseDetailsShowAll';
import { constantFormValues } from './root';
import { encodeQueryParamData } from 'utils/utils';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import ActionButtons from './CustomComponents/cardSections/ActionButtons';
import {
	MemoizedAccountingInformation,
	MemoizedFinancialTermsConditions,
	MemoizedGeneralLeaseInformation,
	MemoizedIFRS16FieldsModifications,
	MemoizedLeaseTable,
} from './CustomComponents/MemoizedComponents';
import { transformRangeDataToAmountArray } from './CustomComponents/cardSections/GrossRent';
import { processArrays } from './CustomComponents/LeaseUtils/helper';
import AgreementFiles from './CustomComponents/cardSections/AgreementFiles';
import LeaseBulkUpload from './CustomComponents/LeaseBulkUpload';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';
import useControlData from './CustomComponents/hooks/useControlData';

const Create = ({ screen = 'CREATE' }) => {
	//constants
	dayjs.extend(utc);

	const [form] = Form.useForm();
	const { state } = useLocation();
	const navigate = useNavigate();
	const formValuesRef = useRef({});
	// Redux selectors
	const { creatorOrganizationId, userId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;
	// Core state variables with proper initialization
	const [leaseControlData, setLeaseControlData] = useState([]);
	const [showAllDataList, setShowAllDataList] = useState([]);
	const [action, setAction] = useState(screen === 'CREATE' ? 'create' : 'edit');
	const [identityData, setIdentityData] = useState({});
	const [calculateData, setCalculateData] = useState({});
	const [leaseChanges, setLeaseChanges] = useState([]);
	const [initialFormValues, setInitialFormValues] = useState({});
	const [activeKey, setActiveKey] = useState('1');

	// Expose necessary UI states for components that need them
	const [isLoadDataButtonDisabled, setIsLoadDataButtonDisabled] = useState(true);
	const [isPopulateScheduleDisabled, setIsPopulateScheduleDisabled] = useState(true);
	const [isSubmitButtonDisable, setIsSubmitButtonDisable] = useState(true);
	const [isLoadDataClick, setIsLoadDataClick] = useState(false);
	const [isPopulateClick, setIsPopulateClick] = useState(false);

	// Modal and action states
	const [showModal, setShowModal] = useState(false);
	const [modalData, setModalData] = useState({ head: state?.processName ?? '', label: 'Do you want to proceed?' });
	const [submitData, setSubmitData] = useState(null);
	const [isActionRequiredButtonClicked, setIsActionRequiredButtonClicked] = useState(false);
	const [reloadData, setReloadData] = useState(false);
	const [showActiveDeactiveModal, setShowActiveDeactiveModal] = useState(false);
	const [showClosedModal, setShowClosedModal] = useState(false);
	const [activeDeactiveSubmitData, setActiveDeactiveSubmitData] = useState(null);
	const [rowIndex, setRowIndex] = useState(0);

	const [approverUserIdFromMeta, setApproverUserIdFromMeta] = useState(null);

	const [selectHistory, setSelectHistory] = useState(null);
	const [missingFields, setMissingFields] = useState(0);

	// API mutations with minimal destructuring
	const [GetSpacialCaseHeader, { data: tableHeader }] = useGetIdentityHeaderMutation();
	const [createSingleIdentity, { error: createError, isSuccess: isCreateSuccess, isError: isCreateError }] = useGetSingleIdentityMutation();
	const [viewSingleIdentity, { data: viewData, isLoading: isViewLoading }] = useGetSingleIdentityMutation();
	const [UpdateSingleIdentity, { error: updateError, isSuccess: isUpdateSuccess, isError: isUpdateError }] = useGetSingleIdentityMutation();
	const [getLeaseTableData, { isLoading: isLeaseTableDataLoading }] = useGetQueryV3Mutation();
	const [sendControlData, { isLoading: isScheduleLoading }] = useSendControlDataMutation();
	const [sendControlDataForShowAll, { isLoading: isSendControlDataLoading }] = useSendControlDataMutation();
	const [IdentityBulk, { data: activationData, isSuccess: isCaseSuccess, isLoading: isCaseLoading, isError: isCaseError }] =
		useIdentityBulkMutation();

	const LeaseEndDate = dayjs().add(1, 'month');
	const statusData = useMemo(() => {
		return Object.keys(viewData?.parkedData ?? {})?.length === 0 ? viewData?.data : viewData?.parkedData;
	}, [viewData]);

	const hasApprovalPermission = approverUserIdFromMeta ? approverUserIdFromMeta === userId : viewData?.approverUserId?.includes(userId);

	const endOfFinantialYearValue = form.getFieldValue('endOfFinancialYear');
	if (!endOfFinantialYearValue) form.setFieldValue('endOfFinancialYear', dayjs().month(11).date(31));
	// const updateFormState = useCallback((updates) => {
	// 	// Batch multiple state updates into a single re-render
	// 	ReactDOM.unstable_batchedUpdates(() => {
	// 		// Apply all state updates at once
	// 		Object.entries(updates).forEach(([key, value]) => {
	// 			switch (key) {
	// 				case 'isLoadDataButtonDisabled':
	// 					setIsLoadDataButtonDisabled(value);
	// 					break;
	// 				case 'isPopulateScheduleDisabled':
	// 					setIsPopulateScheduleDisabled(value);
	// 					break;
	// 				case 'isSubmitButtonDisable':
	// 					setIsSubmitButtonDisable(value);
	// 					break;
	// 				case 'isLoadDataClick':
	// 					setIsLoadDataClick(value);
	// 					break;
	// 				case 'newRowAdded': // For Edit.jsx
	// 					setNewRowAdded(value);
	// 					break;
	// 				case 'isUpdateButtonDisabled': // For Edit.jsx
	// 					setIsUpdateButtonDisabled(value);
	// 					break;
	// 				// Add other state variables as needed
	// 			}
	// 		});
	// 	});
	// }, []);
	const handleSingleLeaseDeactivate = (status) => {
		if (status === 'Closed') {
			setModalData({
				head: state?.processName ?? 'Lease Record',
				action: status,
				label: <p>Do you want to close this lease record?</p>,
			});
		}
		if (status === 'Reopened')
			// Set modal data for confirmation
			setModalData({
				head: state?.processName ?? 'Lease Record',
				action: status,
				label: <p>Do you want to re-open this lease record?</p>,
			});

		// Create a payload with the current lease ID
		const singleLeasePayload = [{ _id: state._id }];
		setActiveDeactiveSubmitData(singleLeasePayload);

		// Show the confirmation modal
		setShowClosedModal(true);
	};

	const { lynFormProperties } = useControlData('10072');

	const isLeaseLifeCycleOver = () => {
		const currentDate = dayjs();
		const leaseEndDate = dayjs(statusData?.leaseEndDate);

		return currentDate.isAfter(leaseEndDate);
	};
	const modalSubmitHandler = () => {
		if (modalData?.action === 'Active') {
			const payload = {
				_id: activeDeactiveSubmitData.map((item) => item._id),
				operation: 'activate-bulk',
				processCode: state?.processCode,
				activationStatus: 'Active',
			};

			IdentityBulk(payload);
		} else if (modalData?.action === 'Inactive') {
			const payload = {
				_id: activeDeactiveSubmitData.map((item) => item._id),
				operation: 'activate-bulk',
				processCode: state?.processCode,
				activationStatus: 'Inactive',
			};

			IdentityBulk(payload);
		} else if (modalData?.action === 'Closed') {
			const payload = {
				_id: activeDeactiveSubmitData.map((item) => item._id),
				operation: 'activate-bulk',
				processCode: state?.processCode,
				activationStatus: 'Closed',
			};

			IdentityBulk(payload);
		} else if (modalData?.action === 'Reopened') {
			const payload = {
				_id: activeDeactiveSubmitData.map((item) => item._id),
				operation: 'activate-bulk',
				processCode: state?.processCode,
				activationStatus: 'Reopened',
			};

			IdentityBulk(payload);
		} else if (modalData?.action === 'Delete') {
			const payload = {
				_id: activeDeactiveSubmitData.map((item) => item._id),
				operation: 'delete-bulk',
				processCode: state?.processCode,
			};

			IdentityBulk(payload);
		} else if (modalData?.action === 'Approve') {
			IdentityBulk({
				_id: activeDeactiveSubmitData,
				operation: 'approve-bulk',
				processCode: state?.processCode,
				suffixCode: '',
			});
		} else if (modalData?.action === 'Reject') {
			IdentityBulk({
				_id: activeDeactiveSubmitData,
				operation: 'reject-bulk',
				processCode: state?.processCode,
			});
		}
		// Hide the confirmation modal
		setShowActiveDeactiveModal(false);
		// navigate('/static/case-identity', {
		// 	state: state,
		// });
	};

	//page utility functions
	const handleCreateClick = () => {
		// Validate all fields

		form.validateFields()
			.then((values) => {
				// console.log('🚀 ~ .then ~ values:', values);
				// All validations passed, proceed with form submission
				// if (!isLoadDataClick) {
				// 	message.error({
				// 		content: 'Please click on the Load New Data to load',
				// 		icon: <img src={cancelIcon} alt="inactive" width="16px" height="16px" style={{ marginRight: '8px', verticalAlign: 'sub' }} />,
				// 	});
				// 	return;
				// }
				handleSubmit(values);
			})
			.catch((errorFields) => {
				// console.log('🚀 ~ handleCreateClick ~ errorFields:', errorFields);
				// console.log('errorFields', errorFields);

				// Validations failed, show error notifications
				if (errorFields && errorFields.length > 0) {
					// Group errors by form section based on your form structure
					const sections = {
						'General Lease Information': ['leaseType', 'leaseTitle', 'lessorName', 'paymentFrequency', 'paymentModality'],
						'Financial Terms & Conditions': [
							// 'advanceAmount',
							'advanceDuration',
							'grossRent',
							'leaseTerm',
							'leaseStartDate',
							'escalationPercentage',
							'initialDiscountRate',
						],
						'Accounting Information': [
							'rightOfUseAssetValue',
							'glleaseLiability',
							'costCenterInterestExpense',
							'glInterestExpense',
							'leaseExpenseCostCenter',
							'leaseExpense',
							'leasePayableControlAccount',
							'glAdvanceRent',
							'depreciationRoUCostCenter',
							'depreciationRoU',
							'accumDepreciationRoU',
							'glBankAccount',
							'securityDepositHead',
							'accountsPayableHead',
						],
						'Modification - IFRS 16-Specific Fields': ['changes'],
					};

					const sectionErrors = {};

					// Categorize errors by section
					// console.log('🚀 ~ errorFields.forEach ~ errorFields:', errorFields);
					errorFields.forEach((field) => {
						// console.log('🚀 ~ errorFields.forEach ~ field:', field);
						const fieldName = field.name[0];

						// Find which section this field belongs to
						let foundSection = false;
						for (const [section, fields] of Object.entries(sections)) {
							if (fields.includes(fieldName)) {
								if (!sectionErrors[section]) {
									sectionErrors[section] = [];
								}
								sectionErrors[section].push({
									name: fieldName,
									errors: field.errors,
								});
								foundSection = true;
								break;
							}
						}

						// If field doesn't match any section, add to "Other Fields" category
						if (!foundSection) {
							if (!sectionErrors['Other Fields']) {
								sectionErrors['Other Fields'] = [];
							}
							sectionErrors['Other Fields'].push({
								name: fieldName,
								errors: field.errors,
							});
						}
					});

					// Create notification message with HTML formatting using the labels from constantFormValues
					toast.error(
						<div>
							<div>Please fill up the required fields</div>
						</div>,
						toastConfig
					);

					// Scroll to the first error field
					const firstErrorField = errorFields[0].name[0];
					const errorFieldElement = document.querySelector(`[id^="rc_select_${firstErrorField}"], [id^="form_item_${firstErrorField}"]`);
					if (errorFieldElement) {
						errorFieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}
				} else {
					toast.error('Please fill all required fields correctly.', toastConfig);
				}
			});
	};
	const isOtherMendatoryFieldsFilled = () => {
		const leaseType = form.getFieldValue('leaseType');
		const leaseTitle = form.getFieldValue('leaseTitle');
		const lessorName = form.getFieldValue('lessorName');
		const upfrontLeasePayment = form.getFieldValue('upfrontLeasePayment');
		const initialDirectCost = form.getFieldValue('initialDirectCost');
		const securityDeposit = form.getFieldValue('securityDeposit');
		const endOfFinancialYear = form.getFieldValue('endOfFinancialYear');
		const rightOfUseAssetValue = form.getFieldValue('rightOfUseAssetValue');
		if (
			!leaseType ||
			!leaseTitle ||
			!lessorName ||
			!upfrontLeasePayment ||
			!initialDirectCost ||
			!securityDeposit ||
			!endOfFinancialYear ||
			!rightOfUseAssetValue
		) {
			return false;
		}
		return true;
	};
	const isRequiredfieldsFilled = () => {
		const leaseTerm = form.getFieldValue('leaseTerm');
		const initialDiscountRate = form.getFieldValue('initialDiscountRate');
		const advanceAmount = form.getFieldValue('advanceAmount');
		const advanceDuration = form.getFieldValue('advanceDuration');

		if (!leaseTerm || !initialDiscountRate || !advanceAmount || !advanceDuration) {
			return false;
		}

		return true;
	};

	const handleSubmit = (data) => {
		// console.log('🚀 ~ handleSubmit ~ data:', data);
		if (action === 'activeDeactivate') {
			setAction('activeDeactivate');

			setModalData({
				head: 'Confirmation',
				label: `Do you want to ${data?.activationStatus === 'Active' ? 'deactivate' : 'activate'}?`,
			});
		} else if (action === 'approve-single') {
			setAction('approve-single');

			setModalData({ head: 'Confirmation', label: 'Do you want to approve?' });
		} else if (action === 'reject-single') {
			setAction('reject-single');

			setModalData({ head: 'Confirmation', label: 'Do you want to reject?' });
		} else if (screen === 'CREATE') {
			setAction('create');
			setModalData({ head: 'Confirmation', label: 'Do you want to create?' });
			const { leaseStartDate, leaseEndDate, endOfFinancialYear, changes } = data;
			data.leaseStartDate = utcConverter(leaseStartDate);
			data.leaseEndDate = utcConverter(leaseEndDate);
			data.modifiedDateTime = utcConverter(leaseStartDate);
			data.initialDiscountRate = data.changes[0].inputChangeDiscountRate;
			data.paymentModalityLabel = data.paymentModality?.toLowerCase() === 'start' ? 'Advance basis' : 'Acrual Basis';
			data.endOfFinancialYear = utcConverter(endOfFinancialYear);
			data.changes = changes.map((change) => {
				return { ...change, inputChangeEffectiveDate: utcConverter(change.inputChangeEffectiveDate) };
			});
			data.lastModifiedDate = utcConverter(dayjs());
			// These were set in the hidden form fields by the modals
			const grossRentList = form.getFieldValue('grossRentList');
			const advanceAmountList = form.getFieldValue('advanceAmountList');
			const grossRents = transformRangeDataToAmountArray(grossRentList);
			const advanceAmounts = transformRangeDataToAmountArray(advanceAmountList);
			data.changes = changes.map((change, index, array) => {
				const { ...changeRest } = change;
				const { processedGrossRents, processedAdvanceAmounts } = processArrays(grossRents, advanceAmounts);
				// Base change object with converted date
				let modifiedChange = {
					...changeRest,
				};
				modifiedChange.inputChangeGrossAmount = 0;
				modifiedChange.inputChangeEscalation = 0;

				modifiedChange.inputChangeGrossAmount = 0;
				modifiedChange.inputChangeEscalation = 0;

				// If this is the last change entry and we have modal data, attach it
				if (index === array.length - 1) {
					if (processedAdvanceAmounts) {
						modifiedChange.inputChangeAdvanceAmounts = processedAdvanceAmounts;
					}
					if (processedGrossRents) {
						modifiedChange.inputChangeGrossRents = processedGrossRents;
					}

					// console.log('Attaching modal data to last change for final update:', modifiedChange);
				}

				return modifiedChange;
			});

			const { processedGrossRents, processedAdvanceAmounts, effectiveAdvanceDuration } = processArrays(grossRents, advanceAmounts);
			data.grossRentListSet = data.grossRentListSet || [];
			data.advanceAmountListSet = data.advanceAmountListSet || [];
			if (processedGrossRents && Array.isArray(processedGrossRents)) {
				data.grossRents = processedGrossRents;

				data.grossRentListSet.push(grossRentList);
				// console.log('Adding grossRents to API payload:', grossRents);
			}

			if (processedAdvanceAmounts && Array.isArray(processedAdvanceAmounts)) {
				data.advanceAmounts = processedAdvanceAmounts;
				data.advanceAmountListSet.push(advanceAmountList);
				// console.log('Adding processedAdvanceAmounts to API payload:', processedAdvanceAmounts);
				data.advanceDuration = effectiveAdvanceDuration;
			}
			// console.log('payload', data);
			setSubmitData(data);
		} else if (screen === 'EDIT') {
			setAction('edit');
			setModalData({ head: 'Confirmation', label: `Do you want to edit?` });

			setSubmitData(data);
		} else {
			return;
		}

		setShowModal(true);
	};

	const utcConverter = (dayjsDate) => {
		// Extract the date part as a string in YYYY-MM-DD format.
		const dateStr = dayjsDate?.format('YYYY-MM-DD');

		return dayjs?.utc(dateStr, 'YYYY-MM-DD');
	};

	const initialDataLoad = async () => {
		if (screen !== 'CREATE' && viewData?.activationStatus?.toLowerCase() !== 'active') {
			let payloadData = {};

			payloadData = {
				advanceAmount: statusData?.advanceAmount,
				grossRent: statusData?.changes[0]?.inputChangeGrossAmount,
				leaseTerm: statusData?.changes[0]?.inputChangeLeaseTerm,
				discountRate: statusData?.changes[0]?.inputChangeDiscountRate,
				startYear: String(dayjs(statusData?.leaseStartDate).$y),
				escalation: statusData?.changes[0]?.inputChangeEscalation,
				startMonth: String(dayjs(statusData?.leaseStartDate).month() + 1),
				paymentFrequency: statusData?.changes[0]?.paymentFrequency,
				paymentTime: statusData?.changes[0]?.paymentTime,
				advanceDuration: statusData?.advanceDuration,
				startDay: String(dayjs(statusData?.leaseStartDate).date()),
				effectiveDate: utcConverter(dayjs(statusData?.changes[0].inputChangeEffectiveDate)),
				advanceAmounts: statusData?.advanceAmounts,
				grossRents: statusData?.grossRents,
				changes: statusData?.changes?.slice(1),
			};

			if (payloadData['advanceAmount'] === null || payloadData['advanceAmount'] === undefined) return;
			setCalculateData(payloadData);

			const convertJson = JSON.stringify(payloadData);
			const convertBase64 = btoa(convertJson);

			const formattedPayload = {
				data: convertBase64,
				processCode: state?.processCode,
				scriptName: 'lease',
				payloadData: payloadData,
			};

			try {
				await sendControlData(formattedPayload).then((res) => {
					setLeaseControlData(res?.data?.responseData);
					handleShowAll();
				});
			} catch (error) {
				return;
			}
		}
	};
	const queryV3Loader = async () => {
		if (screen !== 'CREATE') {
			if (
				+form.getFieldValue('version') &&
				companyCode &&
				(viewData?.activationStatus?.toLowerCase() === 'active' ||
					viewData?.activationStatus?.toLowerCase() === 'closed' ||
					viewData?.activationStatus?.toLowerCase() === 're-open')
			) {
				await getLeaseTableData({
					companyCode,
					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
					fieldValues: [companyCode, state._id, form.getFieldValue('version')],
					name: 'leaseSchedulesOnVersion-v2',
				}).then((res) => {
					setLeaseControlData(res?.data?.results);
				});
			}
		}
	};
	useEffect(() => {
		if (!isActionRequiredButtonClicked) return;
		const fetchData = async () => {
			try {
				await queryV3Loader();

				toast.success('Success! Sent back to ready for processing. ', toastConfig);
			} catch (error) {
				// Handle any errors if needed
				console.error('Error in queryV3Loader:', error);
			}
		};

		// Call the async function
		if (isActionRequiredButtonClicked) {
			fetchData();
		}

		// Optional cleanup function
	}, [isActionRequiredButtonClicked, queryV3Loader]);

	const areAllRecordsRecorded = () => {
		if (!leaseControlData || leaseControlData.length === 0) return false;
		if (viewData?.activationStatus?.toLowerCase() === 'inactive') return false;
		return leaseControlData.every((record) => record.processStatus === 'Recorded');
	};
	const isChangedTabledataFilled = () => {
		const changes = (form.getFieldValue('changes') ?? [])[0];

		if (
			// changes.inputChangeAdvance &&
			changes?.inputChangeAmortizationPeriod &&
			changes?.inputChangeDiscountRate &&
			changes?.inputChangeEffectiveDate &&
			changes?.inputChangeEscalation &&
			changes?.inputChangeGrossAmount &&
			changes?.inputChangeLeaseTerm &&
			changes?.paymentFrequency &&
			changes?.paymentTime
		) {
			return true;
		}
		return false;
	};
	const isFirstRowMatchWithRequiredFields = () => {
		const changes = (form.getFieldValue('changes') ?? [])[0];
		if (
			changes?.inputChangeLeaseTerm === form.getFieldValue('leaseTerm') &&
			changes?.paymentFrequency === form.getFieldValue('paymentFrequency') &&
			changes?.paymentTime === form.getFieldValue('paymentModality') &&
			changes?.inputChangeGrossAmount === form.getFieldValue('grossRent') &&
			changes?.inputChangeEscalation === form.getFieldValue('escalationPercentage') &&
			changes?.inputChangeDiscountRate === form.getFieldValue('initialDiscountRate')
		) {
			return true;
		}
		return false;
	};
	const isSubmitButtonDisabled = useCallback(() => {
		// 1. Check if required financial fields are filled

		if (!isRequiredfieldsFilled()) {
			return true;
		}

		// 2. Check if changes table data is filled
		if (!isChangedTabledataFilled()) {
			return true;
		}

		// 3. Check if data is consistent between form and changes table
		if (!isFirstRowMatchWithRequiredFields()) {
			return true;
		}
		if (!isOtherMendatoryFieldsFilled()) {
			return true;
		}

		// 4. Check if lease schedule data has been generated (button was clicked)
		if (
			!isLoadDataClick
			// || !leaseControlData || leaseControlData.length === 0
		) {
			return true;
		}

		// All validations passed
		return false;
	}, [isRequiredfieldsFilled, isChangedTabledataFilled, isFirstRowMatchWithRequiredFields, isOtherMendatoryFieldsFilled, isLoadDataClick]);
	const handleAddRow = useCallback(() => {
		// console.log('Before Load Data - advanceAmount:', form.getFieldValue('advanceAmount'));
		const currentValues = {
			paymentFrequency: form.getFieldValue('paymentFrequency'),
			paymentTime: form.getFieldValue('paymentModality'),
			inputChangeLeaseTerm: form.getFieldValue('leaseTerm'),
			inputChangeGrossAmount: form.getFieldValue('grossRent'),
			inputChangeDiscountRate: form.getFieldValue('initialDiscountRate'),
			inputChangeEscalation: form.getFieldValue('escalationPercentage'),
			inputChangeAdvance: form.getFieldValue('advanceAmount'),
			inputChangeAmortizationPeriod: form.getFieldValue('advanceDuration'),
			inputChangeEffectiveDate: form.getFieldValue('leaseStartDate'),
		};
		if (screen === 'CREATE') {
			// form.setFieldsValue({ changes: { ...currentValues } });
			form.setFieldsValue({ changes: [currentValues] });
			setIsLoadDataButtonDisabled(true);
		}
		// Update the form values
		const changeTableDataFillled = isChangedTabledataFilled();

		if (changeTableDataFillled) {
			setIsPopulateScheduleDisabled(false);
		}
		const isSubmitDisabled = isSubmitButtonDisabled();

		setIsSubmitButtonDisable(isSubmitDisabled);
		setIsLoadDataClick(true);
		// console.log('After Load Data - advanceAmount:', form.getFieldValue('advanceAmount'));
		// console.log('After Load Data - inputChangeAdvance:', form.getFieldValue('changes')?.[0]?.inputChangeAdvance);
	}, [form, screen, isChangedTabledataFilled, isSubmitButtonDisabled]);

	const handleInputMonthChange = (e) => {
		const effectiveDate = dayjs(e);
		const leaseStart = dayjs(form.getFieldValue('leaseStartDate'));

		let monthDifference = (effectiveDate.year() - leaseStart.year()) * 12 + (effectiveDate.month() - leaseStart.month());

		monthDifference += 1;

		if (monthDifference < 0) {
			monthDifference = 0;
		}

		// form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], leaseStart);
		form.setFieldValue(['changes', rowIndex, 'inputChangeMonth'], monthDifference);
		form.setFieldValue(['changes', rowIndex, 'inputChangeEffectiveDate'], effectiveDate);
	};

	const commonData = useMemo(
		() => ({
			HEAD_CODE_RIGHT_OF_USE_ASSET: form.getFieldValue('rightOfUseAssetValue'),
			HEAD_CODE_LEASE_LIABILITY: form.getFieldValue('glleaseLiability') ?? '',
			HEAD_CODE_PREPAID_RENT: form.getFieldValue('glAdvanceRent') ?? '',
			HEAD_CODE_LEASE_PAYABLE: form.getFieldValue('leasePayableControlAccount') ?? '',
			HEAD_CODE_BANK: form.getFieldValue('glBankAccount') ?? '',
			HEAD_CODE_INTEREST_EXPENSE: form.getFieldValue('glInterestExpense') ?? '',
			HEAD_CODE_DEPRICIATION: form.getFieldValue('depreciationRoU') ?? '',
			HEAD_CODE_ACCUMULATED_DEPRICIATION: form.getFieldValue('accumDepreciationRoU') ?? '',
			lessorName: form.getFieldValue('lessorName') ?? '',
		}),
		[form]
	);
	const handleShowAll = async () => {
		// setIsShowAllButtonClicked(true);
		const payloadData = {
			leaseId: statusData?.leaseId,
		};

		const convertJson = JSON.stringify(payloadData);
		const convertBase64 = btoa(convertJson);

		const formattedPayload = {
			data: convertBase64,
			processCode: state?.processCode,
			scriptName: 'lease-controller-schedule-get-findata',
			payloadData: payloadData,
		};

		await sendControlDataForShowAll(formattedPayload).then((res) => {
			setShowAllDataList(res?.data);
		});
	};

	const loadDataButtonDisabilityCheck = () => {
		if (isRequiredfieldsFilled()) {
			if (isChangedTabledataFilled()) {
				if (isFirstRowMatchWithRequiredFields()) {
					return true;
				} else {
					return false;
				}
			} else {
				return false;
			}
		} else {
			return true;
		}
	};

	const handleFormValueChanges = () => {
		const isDisabled = loadDataButtonDisabilityCheck();
		setIsLoadDataButtonDisabled(isDisabled);
		if (!isLoadDataButtonDisabled) {
			setIsLoadDataClick(false);
		}
		const isSubmitDisabled = isSubmitButtonDisabled();
		setIsSubmitButtonDisable(isSubmitDisabled);
	};
	useEffect(() => {
		const isSubmitDisabled = isSubmitButtonDisabled();
		setIsSubmitButtonDisable(isSubmitDisabled);
	}, [isLoadDataClick]);
	useEffect(() => {
		if (isCaseSuccess) {
			toast.success('Success! Lease status updated successfully', toastConfig);
			// Reload the data to reflect the changes
			setReloadData((prev) => !prev);
			// Close the modal
			setShowActiveDeactiveModal(false);
			const queryParamData = encodeQueryParamData({ processCode: state?.processCode });

			// Navigate back to the case-identity page with the active tab
			navigate(`/case-identity/${queryParamData}?tab=inactive`, {
				replace: true, // This replaces the current entry in the history stack
			});
		} else if (isCaseError) {
			toast.error('Failed! Status update not completed', toastConfig);
			setShowActiveDeactiveModal(false);
		}
	}, [isCaseSuccess, isCaseError]);

	// const SingleUpload = () => (
	// 	<>
	// 		{/* <Form
	// 			form={form}
	// 			layout="vertical"
	// 			onFinish={handleSubmit}
	// 			autoComplete="off"
	// 			requiredMark={false}
	// 			initialValues={{
	// 				rightOfUseAssetValue: '10000250 - RoU Front Offices',
	// 				glleaseLiability: '20001120 - Lease Liabilities',
	// 				costCenterInterestExpense: '902001 - Finance Common',
	// 				glInterestExpense: '40004250 - Interest for Lease',
	// 				leasePayableControlAccount: '20001100 - Lease Payable',
	// 				glAdvanceRent: '10002050 - Advance Rent',
	// 				depreciationRoUCostCenter: '902003 - Finance Common',
	// 				depreciationRoU: '20007555 - Depreciation RoU',
	// 				accumDepreciationRoU: '20004255 - Accum Depreciation RoU',
	// 				glBankAccount: '10003001 - SCB Main',
	// 				leaseExpense: '40004260 - Interest for Lease',
	// 				leaseExpenseCostCenter: '902002 - Finance Common',
	// 				securityDepositHead: '10009008 - Security Deposite',
	// 				accountsPayableHead: '20003300 - Accounts Payable',

	// 				leaseType: 'Finance Lease',
	// 				paymentModality: 'Start',
	// 				paymentFrequency: 'Monthly',
	// 			}}
	// 			// onValuesChange={handleFormValueChanges}
	// 		> */}
	// 		<AntdValuePreview form={form} show={false} />

	// 		{/* </Form> */}

	// 		<Box
	// 			sx={{
	// 				justifyContent: 'space-between',
	// 				gap: '20px',
	// 			}}
	// 		>
	// 			{/* <Spin spinning={isSendControlDataLoading} style={{ maxHeight: '100%', marginTop: '20px' }}> */}

	// 			{/* </Spin> */}
	// 		</Box>
	// 	</>
	// );

	const items = [
		{
			key: '1',
			label: 'Lease Details',
			children: (!isViewLoading && (
				// <MainCard>
				<>
					<CardAccordion
						title="Basic Information"
						darkTitle={true}
						sx={{
							mb: 2,
						}}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<MemoizedGeneralLeaseInformation
							form={form}
							screen={screen}
							constantFormValues={constantFormValues}
							statusData={statusData}
						/>
					</CardAccordion>
					{/* <CardAccordion
						title="Lease Financial & Payment Terms"
						customHeaderSX={{
							backgroundColor: '#e1f0ff !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={true}
						sx={{ mb: 2 }}
					> */}
					<MemoizedFinancialTermsConditions
						screen={screen}
						constantFormValues={constantFormValues}
						// handleLeaseDateChange={handleLeaseDateChange}
						form={form}
						LeaseEndDate={LeaseEndDate}
						statusData={statusData}
					/>
					{/* </CardAccordion> */}
					{/* <CardAccordion
						title="Lease Terms & Payment Schedule"
						customHeaderSX={{
							backgroundColor: '#e1f0ff !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={true}
					>
					</CardAccordion> */}
				</>
				// </MainCard>
			)) || <></>,
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<>
					<CardAccordion
						title="IFRS 16-Specific Fields & Modifications"
						customHeaderSX={{
							fontSize: '16px',
							backgroundColor: '#e1f0ff !important',
							p: '10px',
						}}
						sx={{
							mb: 2,
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={true}
					>
						<MemoizedIFRS16FieldsModifications
							screen={screen}
							constantFormValues={constantFormValues}
							handleInputMonthChange={handleInputMonthChange}
							form={form}
							rowIndex={rowIndex}
						/>

						<ActionButtons
							form={form}
							screen={screen}
							leaseChanges={leaseChanges}
							initialFormValues={initialFormValues}
							setMissingFields={setMissingFields}
							statusData={statusData}
							setCalculateData={setCalculateData}
							handleAddRow={handleAddRow}
							setIdentityData={setIdentityData}
							handleCreateClick={handleCreateClick}
							isLoadDataButtonDisabled={isLoadDataButtonDisabled}
							isPopulateScheduleDisabled={isPopulateScheduleDisabled}
							isSubmitButtonDisable={isSubmitButtonDisable}
							isScheduleLoading={isScheduleLoading}
							viewData={viewData}
							state={state}
							navigate={navigate}
							handleSingleLeaseDeactivate={handleSingleLeaseDeactivate}
							areAllRecordsRecorded={areAllRecordsRecorded}
							isLeaseLifeCycleOver={isLeaseLifeCycleOver}
							selectHistory={setSelectHistory}
							setIsPopulateClick={setIsPopulateClick}
							setLeaseControlData={setLeaseControlData}
							sendControlData={sendControlData}
						/>
					</CardAccordion>
				</>
			),
		},
		{
			key: '3',
			label: 'Accounts Assignment',
			children: (
				<>
					<MemoizedAccountingInformation
						screen={screen}
						constantFormValues={constantFormValues}
						lynFormProperties={lynFormProperties}
						form={form}
					/>
				</>
			),
		},
		{
			key: '4',
			label: 'General Ledgers',
			children: (
				<>
					{/* {screen !== 'CREATE' && showAllDataList?.responseData?.length !== 0 && ( */}
					<CardAccordion
						title="Total Lease View"
						darkTitle={true}
						sx={{
							mt: 2,
							mb: 2,
						}}
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
							fontSize: '16px !important',
						}}
						typographySX={{ fontSize: '16px !important' }}
					>
						<LeaseDetailsShowAll responseData={showAllDataList?.responseData} isLoading={isSendControlDataLoading} />
					</CardAccordion>
					{/* )} */}
				</>
			),
		},
		{
			key: '5',
			label: 'Agreements',
			children: (
				<>
					<CardAccordion
						title="Agreements"
						customHeaderSX={{
							backgroundColor: '#e1f0ff !important',
							p: '10px',
						}}
						typographySX={{ fontSize: '16px !important' }}
						darkTitle={true}
						sx={{ mb: 2 }}
					>
						<AgreementFiles screen={screen} form={form} />
					</CardAccordion>
				</>
			),
		},
		{
			key: '6',
			label: 'Bulk Upload',
			children: (
				<>
					<LeaseBulkUpload />
				</>
			),
		},
		// {
		// 	key: '2',
		// 	label: 'Bulk Upload',
		// 	children: <LeaseBulkUpload />,
		// },
	];

	useEffect(() => {
		if (!isPopulateClick && screen !== 'CREATE' && viewData?.activationStatus?.toLowerCase() !== 'active') {
			initialDataLoad();
		} else {
			handleShowAll();
		}
	}, [statusData]);
	useEffect(() => {
		if (screen === 'CREATE') {
			form.resetFields();
			form.setFieldsValue({
				changes: [{}],
			});
			// setLeaseChanges([{}]);
		}

		if (screen === 'VIEW') {
			const viewValues = {
				// General Lease Information section
				leaseId: statusData?.leaseId,
				leaseType: statusData?.leaseType,
				leaseTitle: statusData?.leaseTitle,
				lessorName: statusData?.lessorName,
				paymentFrequency: statusData?.paymentFrequency,
				paymentModality: statusData?.paymentModality,
				creationDate: dayjs(statusData?.creationDateTime?.localDateTimeStringAMPM) ?? null,
				lastModifiedDate: statusData?.lastModifiedDate ? dayjs(statusData?.lastModifiedDate) : null,

				// Financial Terms & Conditions section
				advanceAmount: statusData?.advanceAmount,
				advanceDuration: statusData?.advanceDuration,
				grossRent: statusData?.grossRent,
				leaseTerm: statusData?.leaseTerm,
				leaseStartDate: dayjs(statusData?.leaseStartDate),
				leaseEndDate: dayjs(statusData?.leaseEndDate),
				escalationPercentage: statusData?.escalationPercentage,
				initialDiscountRate: statusData?.initialDiscountRate,
				upfrontLeasePayment: statusData?.upfrontLeasePayment,
				initialDirectCost: statusData?.initialDirectCost,
				securityDeposit: statusData?.securityDeposit,
				endOfFinancialYear: dayjs(statusData?.endOfFinancialYear),
				// Accounting Information section
				rightOfUseAssetValue: statusData?.rightOfUseAssetValue,
				glleaseLiability: statusData?.glleaseLiability ?? '',
				costCenterInterestExpense: statusData?.costCenterInterestExpense,
				glInterestExpense: statusData?.glInterestExpense,
				leaseExpenseCostCenter: statusData?.leaseExpenseCostCenter,
				leaseExpense: statusData?.leaseExpense,
				leasePayableControlAccount: statusData?.leasePayableControlAccount,
				glAdvanceRent: statusData?.glAdvanceRent,
				depreciationRoUCostCenter: statusData?.depreciationRoUCostCenter,
				depreciationROU: statusData?.depreciationRoU,
				accumDepreciationROU: statusData?.accumDepreciationRoU,
				glBankAccount: statusData?.glBankAccount,
				securityDepositHead: statusData?.securityDepositHead,
				accountsPayableHead: statusData?.accountsPayableHead,

				//others
				version: String(statusData?.version),
				leaseLiability: statusData?.leaseLiability,
				amortizationSchedule: statusData?.amortizationSchedule,
				leaseCategory: statusData?.leaseCategory,
				leaseName: statusData?.leaseName,
				contactDetails: statusData?.contactDetails,

				renewalOptions: statusData?.renewalOptions,
				terminationOption: statusData?.terminationOption,
				month: statusData?.month,
				refundability: statusData?.refundability,
				agreementFiles: statusData?.agreementFiles,

				responsibility: statusData?.responsibility,
				basisOfRate: statusData?.basisOfRate,
				nonLeaseComponents: statusData?.nonLeaseComponents,
				separateServiceCharges: statusData?.separateServiceCharges,
				modificationDetails: statusData?.modificationDetails,
				discountRateChanges: statusData?.discountRateChanges,
				advanceAmounts: statusData?.advanceAmounts,
				grossRents: statusData?.grossRents,
				grossRentList: statusData?.grossRentList,
				advanceAmountList: statusData?.advanceAmountList,
				//changes
				changes: statusData?.changes?.map((item) => {
					const { inputChangeEffectiveDate, ...change } = item;
					return {
						...change,
						inputChangeEffectiveDate: dayjs(inputChangeEffectiveDate),
					};
				}),
			};
			setRowIndex(statusData?.changes?.length);

			form.setFieldsValue(viewValues);
			setInitialFormValues(viewValues);
			setLeaseChanges(statusData?.changes ?? []);
		}
	}, [screen, viewData]);

	useEffect(() => {
		if (screen === 'VIEW') {
			viewSingleIdentity({
				suffixCode: '',
				_id: state?._id ?? '',
				operation: 'get-single',
				processCode: state?.processCode,
			});
		}
	}, [screen, reloadData]);
	useEffect(() => {
		if (!isPopulateClick) {
			queryV3Loader();
		}
	}, [screen, reloadData, form.getFieldValue('version'), companyCode]);

	useEffect(() => {
		if (screen === 'CREATE') {
			if (isCreateSuccess) {
				toast.success('Success! Action completed ', toastConfig);
				navigate(-1);
			}
			if (isCreateError) {
				if (createError?.data?.code === '501') {
					toast.error(createError?.data?.message, toastConfig);
				} else {
					toast.error('Failed! Action not completed', toastConfig);
				}
			}
		}
	}, [
		isCreateSuccess,
		createError,
		isCreateError,
		isUpdateSuccess,
		updateError,
		isUpdateError,
		// isUpdateSingleCaseSuccess,
		// isSingleCaseError,
		screen,
	]);

	useEffect(() => {
		if (state?.processCode) {
			GetSpacialCaseHeader({
				companyCode,
				suffixCode: '',
				operation: 'get-single',
				processCode: state?.processCode,
			});
		}
	}, [companyCode, state]);
	useEffect(() => {
		if (isEmpty(state?.processCode)) {
			navigate(-1);
		}
	}, [state?.processCode]);
	useEffect(() => {
		if (tableHeader && userId) {
			const approverUserIdFromMetaData = tableHeader?.meta?.approverUserId;

			setApproverUserIdFromMeta(approverUserIdFromMetaData);
		}
	}, [tableHeader, userId, hasApprovalPermission]);

	return (
		<Box>
			<DetailPageHeader
				image={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />}
				title={screen === 'CREATE' ? 'Create Lease Record' : 'View Lease Record'}
			/>

			<Spin spinning={isViewLoading} style={{ maxHeight: '100%' }}>
				{/* {screen !== 'CREATE' ? (
					 !isViewLoading && <SingleUpload />
					!isViewLoading && <></>
				) :  */}
				<MainCard sx={{ overflow: 'visible' }}>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						autoComplete="off"
						requiredMark={false}
						initialValues={{
							rightOfUseAssetValue: '10000250 - RoU Front Offices',
							glleaseLiability: '20001120 - Lease Liabilities',
							costCenterInterestExpense: '902001 - Finance Common',
							glInterestExpense: '40004250 - Interest for Lease',
							leasePayableControlAccount: '20001100 - Lease Payable',
							glAdvanceRent: '10002050 - Advance Rent',
							depreciationRoUCostCenter: '902003 - Finance Common',
							depreciationRoU: '20007555 - Depreciation RoU',
							accumDepreciationRoU: '20004255 - Accum Depreciation RoU',
							glBankAccount: '10003001 - SCB Main',
							leaseExpense: '40004260 - Interest for Lease',
							leaseExpenseCostCenter: '902002 - Finance Common',
							securityDepositHead: '10009008 - Security Deposite',
							accountsPayableHead: '20003300 - Accounts Payable',
							grossRentList: [
								{
									from: 1,
									to: '',
									amount: '',
									remarks: '',
								},
							],

							advanceAmountList: [
								{
									from: 1,
									to: '',
									amount: '',
								},
							],

							leaseType: 'Finance Lease',
							paymentModality: 'Start',
							paymentFrequency: 'Monthly',
						}}
						// onValuesChange={handleFormValueChanges}
					>
						<AntdValuePreview form={form} show={true} />
						<Box
							sx={{
								'& .ant-tabs-nav': {
									position: 'sticky !important',
									padding: '20px 0px 10px',
									top: '140px',
									left: '0',
									width: '100%',
									zIndex: '1',
									display: 'block',
									background: '#ffffff',
								},
							}}
						>
							<Tabs
								// activeKey={activeKey}
								defaultActiveKey="1"
								items={items}
								type="card"
								// tabBarExtraContent={{ right: <Button>Right Extra Action</Button> }}
								onChange={(key) => {
									setActiveKey(key);
								}}
							/>
						</Box>
					</Form>
					{activeKey === 2 && (
						<CardAccordion
							title="Lease Schedule"
							customHeaderSX={{
								fontSize: '16px',
								backgroundColor: '#e1f0ff !important',
								p: '10px',
							}}
							typographySX={{ fontSize: '16px !important' }}
							darkTitle={true}
						>
							<Spin spinning={isLeaseTableDataLoading || isSendControlDataLoading}>
								{leaseControlData?.length > 0 && (
									<MemoizedLeaseTable
										leaseId={+statusData?.leaseId}
										leaseResponseData={leaseControlData ?? []}
										commonData={commonData}
										screen={screen}
										identityData={screen === 'CREATE' ? identityData : initialFormValues}
										calculateData={calculateData}
										initialFormValues={initialFormValues}
										statusData={statusData}
										viewData={viewData}
										form={form}
										setIsActionRequiredButtonClicked={setIsActionRequiredButtonClicked}
									/>
								)}
							</Spin>
						</CardAccordion>
					)}
				</MainCard>
			</Spin>
			{showActiveDeactiveModal && (
				<ComptrollerConfirmModal
					showModal={showActiveDeactiveModal}
					setShowModal={setShowModal}
					modalData={modalData}
					handleSubmit={modalSubmitHandler}
					loading={isCaseLoading}
				/>
			)}
			{/* .............. */}
			{showClosedModal && (
				<ComptrollerConfirmModal
					showModal={showClosedModal}
					setShowModal={setShowClosedModal}
					modalData={modalData}
					handleSubmit={modalSubmitHandler}
					// loading={isCaseLoading}
				/>
			)}

			{showModal && (
				<ComptrollerConfirmModal
					showModal={showModal}
					modalData={modalData}
					setShowModal={setShowModal}
					handleSubmit={() =>
						action === 'approve-single' || action === 'reject-single'
							? null
							: action === 'create'
								? createSingleIdentity({
										operation: 'add-single',
										processCode: state?.processCode,
										suffixCode: '',
										data: submitData,
									})
								: UpdateSingleIdentity({
										operation: 'update-single',
										processCode: state?.processCode,
										suffixCode: '',
										data: submitData,
										_id: state._id,
										version: +viewData?.version,
									})
					}
				/>
			)}
			{selectHistory && (
				<IdentityCaseActivityModal
					title={state?.processName}
					setSelectHistory={setSelectHistory}
					selectItem={selectHistory}
					processCode={viewData?.processCode}
					// processVersion={processVersion}
					processVersion={viewData?.parkProcessVersion}
				/>
			)}
		</Box>
	);
};

export default Create;
